/**
 * Currency formatting and parsing utilities used by the calculation engine.
 * Ported from frontend/src/utils/currency.js — omits `isCurrencyField` (a form-label heuristic,
 * UI-only, never called by calculations/).
 */

/**
 * A fact-find money field as a plain number, 0 when it is absent or unreadable.
 *
 * `FactFind` types every money field `number`, and the REST boundary coerces to match (see
 * factFindNumericSchema.ts) — but in-process callers are not obliged to, so some paths still hand over
 * the numeric STRING a form input produced. This is the one place that discrepancy is handled.
 *
 * It replaced a scattering of `parseFloat(asset.value)` calls, which typechecked only while the
 * surrounding parameter was `any`: once a fact-find is typed, passing a `number` to `parseFloat` is an
 * error, and "coerce a value the types already call a number" is the wrong thing to write anyway.
 * Behaviour is unchanged — a number passes through, a numeric string parses, anything else is 0.
 */
export function amountOf(value: unknown): number {
    const amount = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
    return Number.isFinite(amount) ? amount : 0;
}

/** Parse user input (e.g. "$1,234") to numeric string for storage. */
export function parseCurrency(value: string | number | null | undefined): string {
    if (!value || value === '') return '';
    const cleanValue = String(value).replace(/[$,\s]/g, '');
    const numValue = parseFloat(String(cleanValue));
    return isNaN(numValue) ? '' : numValue.toString();
}

/** Format a number for display (e.g. 1234 -> "$1,234"). */
export function formatCurrency(value: number | string | null | undefined): string {
    if (value === null || value === undefined || isNaN(value as number)) {
        return '$0';
    }
    const num = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
    const roundedValue = Math.round(num);
    return `${roundedValue < 0 ? '-' : ''}$${Math.abs(roundedValue).toLocaleString()}`;
}

/**
 * Format a currency amount as a "nominal value" for the human-readable calculation breakdown:
 * the usual `$1,234` formatting wrapped in an `<nv>` tag (short for 'nominal value'). The UI will
 * later be able to toggle every `<nv>` value between nominal and real (inflation-adjusted) display.
 */
export function formatNominalValue(value: number | string | null | undefined): string {
    return `<nv>${formatCurrency(value)}</nv>`;
}

/** Format value for display inside an input field (handles empty). */
export function formatCurrencyForInput(value: string | number | null | undefined): string {
    if (value === '' || value === undefined || value === null) return '';
    const n = Number(value);
    if (isNaN(n)) return '';
    return '$' + n.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
